import { useState, useEffect, useRef } from 'react'

const dataCache = {}

const safeFetch = (url, method = `GET`) =>
  new Promise((resolve, reject) => {
    const req = new XMLHttpRequest()
    req.open(method, url, true)
    req.onreadystatechange = () => {
      if (req.readyState === 4) resolve(req)
    }
    req.send(null)
  })

const getSafeData = response => {
  let data = null
  const { status, responseText } = response
  if (status === 200) {
    try {
      data = JSON.parse(responseText)
    } catch (err) {
      data = { error: err.message }
    }
  } else data = { error: 'status:' + status }
  return data
}

const aServiceTask = async (serviceUrl, body, useCache, headers, method, doSafe, hookName, contentType) => {
  let data = null
  const lastcache = useCache === 'last'
  const urlCache = dataCache[serviceUrl] || {}
  const cacheId = method + (body || '') + (headers || '')
  if (lastcache) data = urlCache.last === cacheId && urlCache[cacheId]
  else if (useCache) data = urlCache[cacheId]
  if (!data) {
    if (useCache) dataCache[serviceUrl] = { ...urlCache, [cacheId]: { resolvers: [] }, last: cacheId }
    try {
      headers = headers ? { ...JSON.parse(headers) } : {}
      if (contentType) headers['Content-Type'] = contentType
      const myfetch = doSafe ? safeFetch : fetch
      const encodedUrl = encodeURI(serviceUrl)
      const myfetchParams = doSafe ? [encodedUrl, method] : [encodedUrl, body ? { method, body, headers } : { headers }]
      const response = await myfetch(...myfetchParams)
      if (doSafe) data = getSafeData(response)
      else data = await response.json()
      if (!data.status && response.status) data.status = response.status
    } catch (err) {
      if (err.message !== 'Failed to fetch') console.log('error', err.message, serviceUrl)
      data = { error: err.message }
    }
    if (useCache) {
      if (dataCache[serviceUrl][cacheId] && dataCache[serviceUrl][cacheId].resolvers) {
        dataCache[serviceUrl][cacheId].resolvers.map(resolve => resolve(data))
      }
      dataCache[serviceUrl][cacheId] = data
      dataCache[serviceUrl].last = cacheId
    }
  } else if (data.resolvers) data = await new Promise(resolve => data.resolvers.push(resolve))
  else if (process.env.ICO_ENV !== 'prod') {
    if (lastcache) console.log('Using response from last for request: ', hookName, ' (avoid change in hook dependencies!)')
    else if (useCache) console.log('Using response from cache for request: ', serviceUrl, ' (avoid change in hook dependencies!)')
  }
  return data
}

export default (serviceUrl, useCache, body, headers, method = 'get', hookName, contentType = 'text/json', doSafe, cancelRequest = false) => {
  const [data, setData] = useState()
  const [isFetching, setIsFetching] = useState(false)
  const [fetchCount, setFetchCount] = useState(1)
  const resetData = () => setData()
  const fetchData = () => setFetchCount(fetchCount + 1)
  useEffect(() => {
    //Don't make http request if cancelRequest is true
    if (cancelRequest) return
    let unmounted = false
    const func = async () => {
      let data = null
      const aServiceUrls = serviceUrl.split(';')
      const serviceTasks = aServiceUrls.map(aServiceUrl => aServiceTask(aServiceUrl, body, useCache, headers, method, doSafe, hookName, contentType))
      data = await Promise.all(serviceTasks)
      if (!unmounted) {
        setData(data.length === 1 ? data[0] : data)
        //!!!! It is important to setFetching after setting data or else !!!!!!!!!
        setIsFetching(false)
      }
    }
    if (serviceUrl && fetchCount) {
      setIsFetching(true)
      func()
    }
    //This forces a cache clear. Implemented to stop data from persisting were it shouldnt
    if (!serviceUrl) resetData()
    return () => (unmounted = true)
  }, [serviceUrl, fetchCount, useCache, body, headers, method, hookName, doSafe, contentType])

  const currentServiceInfoCount = useRef(0)
  const currentServiceInfo = useRef()
  const serviceInfo = serviceUrl ? serviceUrl + method + headers + body : null
  const change = serviceInfo !== currentServiceInfo.current
  if (change) currentServiceInfoCount.current = currentServiceInfoCount.current + 1
  const isFetchingOrUrlChange = serviceUrl && (change || isFetching)
  currentServiceInfo.current = serviceInfo
  return [data, isFetchingOrUrlChange, fetchData, resetData, currentServiceInfoCount.current]
}
